import React, { useEffect, useState } from 'react';
import { getAuthToken } from '../../auth/context/AuthContext';
import Pagination from '../../../shared/components/Pagination';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/platform`;

function createdLabel(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export default function AiCharges() {
  const [companyId, setCompanyId] = useState('');
  const [page, setPage] = useState(1);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const params = new URLSearchParams({ page: String(page) });
        if (companyId) params.set('company_id', companyId);
        const res = await fetch(`${API}/ai-charges?${params}`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not load AI charges');
        if (!gone) {
          setReport(data);
          setErr('');
        }
      } catch (e) {
        if (!gone) setErr(e.message);
      }
    })();
    return () => { gone = true; };
  }, [companyId, page]);

  const total = report?.total || 0;
  const perPage = report?.per_page || 25;
  const pageCount = Math.max(1, Math.ceil(total / perPage));
  const from = total === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">AI charges</div>
          <div className="page-subtitle">
            Each charge taken from a company wallet. Cost in $ is the measured API cost.
          </div>
        </div>
      </div>
      {err && <div className="alert alert-error">{err}</div>}
      {!report && !err && <div className="empty-state-text">Loading…</div>}
      {report && (
        <div className="card" style={{ padding: 16 }}>
          <div className="form-group" style={{ maxWidth: 320 }}>
            <label className="form-label" htmlFor="ai-charges-company">Company</label>
            <select
              id="ai-charges-company"
              className="form-input"
              value={companyId}
              onChange={(e) => {
                setCompanyId(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All companies</option>
              {(report.companies || []).map((company) => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
          </div>
          {total === 0 ? (
            <div className="empty-state-text">No AI charges yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table" style={{ width: '100%', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>When</th>
                    <th>Reason</th>
                    <th>Job</th>
                    <th>Candidate</th>
                    <th>Charged</th>
                    <th>Cost in $</th>
                    <th>Cost in ₹</th>
                    <th>Markup</th>
                  </tr>
                </thead>
                <tbody>
                  {(report.charges || []).map((row) => (
                    <tr key={row.id}>
                      <td style={{ fontWeight: 600 }}>{row.company_name}</td>
                      <td>{createdLabel(row.created_at)}</td>
                      <td>{row.label}</td>
                      <td>{row.job_title || '—'}</td>
                      <td>{row.candidate_name || '—'}</td>
                      <td>{row.charged_display}</td>
                      <td>{row.cost_usd_display || '—'}</td>
                      <td>{row.inr_cost_display || '—'}</td>
                      <td>{row.ai_markup}×</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                page={page}
                pageCount={pageCount}
                from={from}
                to={to}
                total={total}
                noun="charges"
                onChange={setPage}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
